import { createId } from '@paralleldrive/cuid2';

import type { WeeklyTrailer, WeeklyTrip, WeeklyTripStatus } from '../../models';

/** A trip as the day panel edits it: km stays a string until it is saved. */
export interface TripDraft {
  key: string;
  loading: string;
  loadingStatus: WeeklyTripStatus | null;
  unloading: string;
  unloadingStatus: WeeklyTripStatus | null;
  km: string;
}

export function createTripDraft(trip?: WeeklyTrip): TripDraft {
  return {
    key: trip?.id ?? createId(),
    loading: trip?.loading ?? '',
    loadingStatus: trip?.loadingStatus ?? null,
    unloading: trip?.unloading ?? '',
    unloadingStatus: trip?.unloadingStatus ?? null,
    km: trip ? String(trip.km) : '',
  };
}

/** An empty day opens with one blank trip, ready to type in. */
export function toTripDrafts(trips: WeeklyTrip[]): TripDraft[] {
  return trips.length > 0 ? trips.map(createTripDraft) : [createTripDraft()];
}

/** A status alone does not make a trip. */
export function isBlankDraft(draft: TripDraft): boolean {
  return draft.loading.trim() === '' && draft.unloading.trim() === '' && draft.km.trim() === '';
}

/** Whole, non-negative km; an empty field counts as 0. `null` when the input is not a number. */
export function parseDraftKm(km: string): number | null {
  const trimmed = km.trim();
  if (trimmed === '') return 0;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** Blank trips are dropped on save, so they never count as a change. */
export function hasUnsavedChanges(drafts: TripDraft[], saved: WeeklyTrip[]): boolean {
  const comparable = (list: TripDraft[]) =>
    JSON.stringify(
      list
        .filter((d) => !isBlankDraft(d))
        .map((d) => [d.loading, d.loadingStatus, d.unloading, d.unloadingStatus, d.km]),
    );
  return comparable(drafts) !== comparable(saved.map(createTripDraft));
}

export function isSameTrailer(a: WeeklyTrailer | null, b: WeeklyTrailer | null): boolean {
  return (a?.id ?? null) === (b?.id ?? null);
}
